import { useNavigate } from "react-router-dom";
import { ChallengeCard } from "./ChallengeCard";
import { TrendyChallengeCard } from "./TrendyChallengeCard";
import { TrendyEmptyState } from "./TrendyEmptyState";
import { getChallengeImage } from "@/utils/challengeHelpers";
import { HomeChallengeCard } from "@/api/home";

interface ActiveChallengesListProps {
  sectionTitle: string;
  sectionType: "user" | "upcoming" | "active" | "empty";
  challenges: HomeChallengeCard[];
  onChallengeClick?: (challengeId: string, isUserParticipant: boolean, status: string) => void;
  /** Use trendy card style (for first-time users) */
  variant?: "default" | "trendy";
}

export function ActiveChallengesList({
  sectionTitle,
  sectionType,
  challenges,
  onChallengeClick,
  variant = "default",
}: ActiveChallengesListProps) {
  const navigate = useNavigate();
  const isTrendy = variant === "trendy";

  // Empty state
  if (sectionType === "empty" || challenges.length === 0) {
    if (isTrendy) {
      // For trendy variant, show engaging empty state with runners illustration
      return <TrendyEmptyState onCreateClick={() => navigate("/create-challenge")} />;
    }

    return (
      <div className="flex flex-col gap-5">
        <h2 className="px-4 text-2xl font-bold leading-tight tracking-[-0.015em] text-slate-900 dark:text-white">
          {sectionTitle}
        </h2>
        <div className="mx-4 flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 dark:border-white/20 bg-black/5 dark:bg-white/5 p-8 text-center">
          <span className="material-symbols-outlined text-4xl text-slate-400 dark:text-white/40 mb-3">
            fitness_center
          </span>
          <p className="text-slate-600 dark:text-slate-400 mb-1">
            No challenges yet
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500">
            Discover or create your first challenge!
          </p>
        </div>
      </div>
    );
  }

  // Trendy variant - content only (parent provides panel wrapper)
  if (isTrendy) {
    return (
      <div className="pt-4 pb-4">
        {/* Header with See All link */}
        <div className="flex items-center justify-between px-4 mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white font-dm-sans">
            {sectionTitle}
          </h2>
          <button
            onClick={() => navigate("/challenges")}
            className="flex items-center gap-1 text-primary-dark dark:text-[#9dfc29] text-xs font-medium capitalize font-dm-sans hover:brightness-110 transition-all"
          >
            See All
            <span
              className="material-symbols-outlined text-sm"
              style={{ fontSize: "14px" }}
            >
              chevron_right
            </span>
          </button>
        </div>

        {/* Challenge Cards - Horizontal Scroll */}
        <div className="overflow-x-auto pl-4 pr-4 py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex items-stretch gap-4">
            {challenges.map((challenge) => (
              <TrendyChallengeCard
                key={challenge.id}
                id={challenge.id}
                name={challenge.name}
                type={challenge.type}
                totalDays={challenge.totalDays}
                dailyGoal={challenge.dailyGoal}
                onClick={() =>
                  onChallengeClick?.(challenge.id, challenge.isUserParticipant, challenge.status)
                }
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Default variant - original design
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="px-4 text-2xl font-bold leading-tight tracking-[-0.015em] text-slate-900 dark:text-white">
          {sectionTitle}
        </h2>
        <div className="overflow-x-auto pl-4 pr-4 py-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex items-stretch gap-4">
            {challenges.map((challenge) => (
              <ChallengeCard
                key={challenge.id}
                title={challenge.name}
                imageUrl={getChallengeImage(challenge.type)}
                currentDay={challenge.currentDay}
                totalDays={challenge.totalDays}
                progressPercentage={challenge.progressPercentage}
                status={challenge.status}
                isUserParticipant={challenge.isUserParticipant}
                startDate={challenge.startDate}
                participantCount={challenge.participantCount}
                onClick={() =>
                  onChallengeClick?.(challenge.id, challenge.isUserParticipant, challenge.status)
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
